import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from '@app/store';
import {
  fetchRoleById,
  createRole,
  updateRole,
  RoleActions,
} from "./RoleSlice";
import { fetchAffiliates } from "../affiliate/AffiliateSlice";
import { fetchUserProfile } from "../auth/authSlice";
import { RoleMapper } from "./RoleMapper";
import { RoleOutputDTO } from './types';
import { AffiliateOutputDTO } from "../affiliate/types";
import { DataForm } from '@components/ui';
import { Field } from '@components/ui/DataForm';
import { FormSkeleton } from '@components/ui/FormSkeleton';
import { ApiError } from '@/types/api';
import { useNotification } from '@hooks/useNotification';
import { Box, Typography, Alert } from "@mui/material";
import { useAuth } from '@hooks/useAuth';
import { UserRole } from "../user";

const RoleForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.role
  );
  const { items: affiliates, loading: affiliatesLoading } = useSelector(
    (state: RootState) => state.affiliate
  );
  // Get current role info from auth state
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<Partial<RoleOutputDTO>>({});
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notification = useNotification();

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);

  // Make sure we have the current role data
  useEffect(() => {
    if (isAuthenticated && !user) {
      console.log("🔄 Fetching current role profile");
      dispatch(fetchUserProfile());
    }
  }, [dispatch, isAuthenticated, user]);

  // Debug output for current role
  useEffect(() => {
    console.log("🧪 Current authenticated role:", user);
  }, [user]);

  // Role role-based access control
  const currentRoleRole = user?.role as UserRole;
  
  // Redirect lawyer roles away from this form
  useEffect(() => {
    if (currentRoleRole === UserRole.LAWYER) {
      notification.showError("Você não tem permissão para acessar esta página");
      navigate("/");
    }
  }, [currentRoleRole, navigate, notification]);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }

      try {
        console.log("📡 Iniciando carregamento do Usuário:", id);
        isLoadingRef.current = true;

        // Load affiliates
        console.log("📡 RoleForm - Carregando Afiliados");
        await dispatch(fetchAffiliates({}));

        if (id) {
          console.log("📡 RoleForm - Carregando Usuário:", id);
          const resultAction = await dispatch(fetchRoleById(id));

          if (fetchRoleById.fulfilled.match(resultAction)) {
            console.log("✅ Usuário carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar dados");
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar Usuário:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();

    return () => {
      console.log("🧹 Limpando estado do formulário");
      dispatch(RoleActions.clearCurrentItem());
    };
  }, [dispatch, id, notification]);

  useEffect(() => {
    if (currentItem) {
      console.log("📝 Role - Atualizando dados do formulário:", currentItem);
      setFormData({
        name: currentItem.name,
        enterpriseId: currentItem.enterpriseId,
      });
    }
  }, [currentItem]);

  // Filter available role options based on current role's role
  

  // Determine if enterprise_id field should be disabled
  const isEnterpriseIdDisabled = () => {
    // Only ADMIN can modify enterprise_id
    return currentRoleRole !== UserRole.ADMIN && !!formData.enterpriseId;
  };

  const fields: Field[] = [
    {
      name: "name",
      label: "Nome",
      type: "text",
      required: true,
      validationError: formError?.validationErrors?.name?.message,
    },
   
    {
      name: "enterpriseId",
      label: "Empresa",
      type: "select",
      required: true,
      disabled: isEnterpriseIdDisabled(),
      options: affiliates.map((affiliate: AffiliateOutputDTO) => ({
        value: affiliate._id || "",
        label: affiliate.name || "Sem nome",
      })),
      validationError: formError?.validationErrors?.enterpriseId?.message,
    },
  ];

  const handleSubmit = async (data: Partial<RoleOutputDTO>) => {
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);
      console.log(currentRoleRole)
      // If password is empty in edit mode, remove it from the payload
      
      // If the role is a Manager trying to create an Admin, prevent it

      // If the role is a Manager, keep the existing enterpriseId or set the current role's enterpriseId
      // if (currentRoleRole === UserRole.ADMIN || currentRoleRole === UserRole.SUPER || currentRoleRole === UserRole.MANAGER) {
      //   if (id && formData.enterpriseId) {
      //     // Keep the original enterprise ID when editing
      //     data.enterpriseId = formData.enterpriseId;
      //   } else if (!id) {
      //     // Set the manager's own enterprise ID for new roles
      //     data.enterpriseId = role?.enterpriseId;
      //   }
      // }

      const mappedData = RoleMapper.toDTO(data);
      console.log("📤 Enviando dados:", mappedData);

      const resultAction = id
        ? await dispatch(updateRole({ id, data: mappedData }))
        : await dispatch(createRole(mappedData));

      if (
        updateRole.rejected.match(resultAction) ||
        createRole.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        setFormError(error);
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      navigate("/roles");
    } catch (error) {
      console.error("❌ RoleForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  // Return nothing if role is a lawyer (will be redirected)
  if (currentRoleRole === UserRole.LAWYER) {
    return null;
  }

  if (isInitialLoad && (loading || affiliatesLoading)) {
    return <FormSkeleton fields={6} />;
  }

  return (
    <Box sx={{ p: 0 }}>
      {/* <Typography variant="h5" gutterBottom>
        {id ? "Editar Função" : "Nova Função"}
      </Typography> */}
      
      {currentRoleRole === UserRole.MANAGER && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Como gerente, você não pode criar usuários administradores nem alterar a empresa vinculada ao usuário.
        </Alert>
      )}
      
      <DataForm
        data={formData}
        fields={fields}
        isEdit={id!=undefined}
        onSubmit={handleSubmit}
        basePath="roles"
        title={id ? "Editar Função" : "Nova Função"}
        error={formError}
        onClearError={() => setFormError(null)}
        disabled={isSubmitting}
      />
    </Box>
  );
};

export default RoleForm;
